import { HostInfo, HostMetadataValue } from "./types";

/**
 * Regras por dispositivo: a primeira regra cujo `match` bate com o HostInfo vale.
 * Todo critério informado precisa bater (E); listas aceitam qualquer valor (OU).
 * Textos são comparados sem diferenciar maiúsculas; `model` aceita RegExp.
 */
export interface VersionRange {
  /** inclusive, ex.: "10" ou "7.1.2" */
  min?: string;
  /** inclusive */
  max?: string;
}

export interface DeviceRuleMatch {
  platform?: string | string[];
  hostType?: string | string[];
  family?: string | string[];
  manufacturer?: string | string[];
  model?: string | string[] | RegExp;
  osVersion?: VersionRange;
  hostVersion?: VersionRange;
  apiLevel?: { min?: number; max?: number };
  metadata?: Record<string, HostMetadataValue>;
}

export interface DeviceRule {
  /** Identificador estável: vai no parâmetro de redirect e nos logs. */
  id: string;
  match: DeviceRuleMatch;
  /** Abre outra URL no lugar do app (ex.: build específico para um modelo). */
  redirectUrl?: string;
  /** Flags livres para o app consultar com `getActiveDeviceRule()`. */
  flags?: Record<string, HostMetadataValue>;
}

/**
 * Regras ativas. Vazio = nenhum comportamento por modelo (o boot nem espera o HostInfo).
 *
 * Exemplo:
 *   {
 *     id: "elgin-android7",
 *     match: { family: ["elgin", "elginmk"], osVersion: { max: "7.1.2" } },
 *     redirectUrl: "https://totem-app-android-legacy.meep.cloud/",
 *   }
 */
export const DEVICE_RULES: DeviceRule[] = [];

const toList = (value: string | string[]): string[] => (Array.isArray(value) ? value : [value]);

const equalsAny = (actual: string | undefined, expected: string | string[] | undefined): boolean => {
  if (expected === undefined) return true;
  const text = (actual || "").toLowerCase();
  return toList(expected).some((item) => item.toLowerCase() === text);
};

/** Compara "10.0.1" com "10": negativo se a < b. Partes não numéricas valem 0. */
export const compareVersions = (a: string, b: string): number => {
  const pa = a.split(/[.\-_]/).map((part) => parseInt(part, 10) || 0);
  const pb = b.split(/[.\-_]/).map((part) => parseInt(part, 10) || 0);
  const length = Math.max(pa.length, pb.length);
  for (let i = 0; i < length; i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

const inVersionRange = (actual: string, range: VersionRange | undefined): boolean => {
  if (!range) return true;
  // Sem versão conhecida não dá para afirmar que está na faixa.
  if (!actual) return false;
  if (range.min && compareVersions(actual, range.min) < 0) return false;
  if (range.max && compareVersions(actual, range.max) > 0) return false;
  return true;
};

const matchesModel = (actual: string, expected: DeviceRuleMatch["model"]): boolean => {
  if (expected === undefined) return true;
  if (expected instanceof RegExp) return expected.test(actual);
  return equalsAny(actual, expected);
};

export const matchesRule = (info: HostInfo, match: DeviceRuleMatch): boolean => {
  if (!equalsAny(info.platform, match.platform)) return false;
  if (!equalsAny(info.host.type, match.hostType)) return false;
  if (!equalsAny(info.device.family, match.family)) return false;
  if (!equalsAny(info.device.manufacturer, match.manufacturer)) return false;
  if (!matchesModel(info.device.model, match.model)) return false;
  if (!inVersionRange(info.os.version, match.osVersion)) return false;
  if (!inVersionRange(info.host.version, match.hostVersion)) return false;
  if (match.apiLevel) {
    const level = info.os.apiLevel;
    if (level === undefined) return false;
    if (match.apiLevel.min !== undefined && level < match.apiLevel.min) return false;
    if (match.apiLevel.max !== undefined && level > match.apiLevel.max) return false;
  }
  if (match.metadata) {
    const ok = Object.entries(match.metadata).every(([key, value]) => info.metadata[key] === value);
    if (!ok) return false;
  }
  return true;
};

export const findDeviceRule = (info: HostInfo, rules: DeviceRule[]): DeviceRule | null =>
  rules.find((rule) => matchesRule(info, rule.match)) || null;
